/**
 * Query `meta` the global QueryCache error handler (App.tsx) honours.
 *
 * A poll that runs every few seconds through a flaky link (the Create Batch
 * status poll, every 2 s through the China proxy) fails often and harmlessly:
 * the next poll is the retry. Reporting each failure to Sentry would bury
 * real errors, so such a query sets `meta: SKIP_GLOBAL_ERROR_REPORT_META` and
 * its failures become a breadcrumb instead. Every other query is reported
 * exactly as before.
 */
import * as Sentry from '@sentry/react';

export const SKIP_GLOBAL_ERROR_REPORT_META = { skipGlobalErrorReport: true } as const;

interface QueryLike {
  meta?: Record<string, unknown>;
  queryKey: readonly unknown[];
}

/**
 * For the global QueryCache `onError`: when `query` opted out of error
 * reporting, leaves a breadcrumb and returns true (the handler stops there).
 * Returns false, doing nothing, for every other query.
 */
export function breadcrumbInsteadOfReport(error: unknown, query: QueryLike): boolean {
  if (query.meta?.skipGlobalErrorReport !== true) return false;
  Sentry.addBreadcrumb({
    category: 'query.poll',
    level: 'warning',
    message: error instanceof Error ? error.message : String(error),
    data: { queryKey: JSON.stringify(query.queryKey) },
  });
  return true;
}
